import styled from "@emotion/styled";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { addMonths } from "date-fns";
import { useFormik } from "formik";
import { FC, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import useAuth from "src/hooks/useAuth";
import useQuery from "src/hooks/useQuery";
import gtm from "src/lib/gtm";
import { apiPost } from "src/services/apiService";
import * as Yup from "yup";

const SocialIcon = styled("img")(() => ({
  width: 24,
}));

const errorDict = new Proxy(
  {
    UserAlreadyExistsException: "forms:user_already_exists",
  },
  {
    get(target, prop) {
      return target[prop] || prop;
    },
  }
);

const blockedDomains = ["uorak.com", "example.com"];

const RegisterForm: FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation(["login_register", "forms"]);
  const { register, login } = useAuth();
  const [cookies, setCookie, removeCookie] = useCookies(["linkId"]);
  const navigate = useNavigate();
  const recaptcha: any = useRef();
  const [linkId, setLinkId] = useState<string | null>(null);

  const query = useQuery();

  useEffect(() => {
    const linkIdFromUrl = query.get("linkId");

    gtm.push({ event: "page_view" });

    if (linkIdFromUrl) {
      const expires = addMonths(new Date(), 1);
      setLinkId(linkIdFromUrl);
      setCookie("linkId", linkIdFromUrl, { path: "/", expires });
      apiPost("/partner/linkClick", {}, { linkId: linkIdFromUrl });
    } else if (cookies.linkId) {
      setLinkId(cookies.linkId);
    }
  }, []);

  const formik = useFormik({
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(t("forms:validation.email.invalid"))
        .max(255, t("forms:validation.email.greaterThanLimit"))
        .required(t("forms:validation.email.required"))
        .test(
          "is-not-blocked-domain",
          t("forms:validation.email.blockedDomain"),
          (value) => {
            if (!value) return true;
            const domain = value.split("@")[1];
            return !blockedDomains.includes(domain);
          }
        ),
      password: Yup.string()
        .min(7, t("forms:validation.password.lessThanMinimum"))
        .max(255, t("forms:validation.password.greaterThanLimit"))
        .required(t("forms:validation.new_password.required")),
      passwordConfirmation: Yup.string().oneOf(
        [Yup.ref("password"), null],
        t("forms:validation.password.mismatched")
      ),
      linkId: Yup.string(),
    }),
    initialValues: {
      name: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      submit: null,
    },
    async onSubmit(values, { setErrors, setStatus, setSubmitting }) {
      try {
        const captchaCode = recaptcha?.current?.getValue();
        if (!captchaCode) {
          alert(t("recaptcha_check"));
          return;
        }

        await register({
          name: values.name,
          email: values.email,
          password: values.password,
          linkId,
        });
        await login({
          email: values.email,
          password: values.password,
          captchaCode,
        });
        setStatus({ success: true });
        setSubmitting(false);
      } catch (err) {
        if (err === "User has ON_HOLD status.") {
          navigate("/authentication/waiting_list", {
            state: {
              email: values.email,
            },
          });
        }

        if (err === "UserNotActiveException") {
          navigate("/email-confirmation");
        }

        setStatus({ success: false });
        setErrors({
          submit: t(errorDict[err]),
        });
        setSubmitting(false);
        recaptcha?.current?.reset();
      }
    },
  });

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  return (
    <>
      <Stack
        component="form"
        role="tabpanel"
        aria-labelledby="simple-tab-register"
        spacing={2}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <TextField
          id="email"
          label={t("email")}
          variant="outlined"
          name="email"
          error={Boolean(formik.touched.email && formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          type="email"
          value={formik.values.email}
        />
        <FormControl>
          <InputLabel htmlFor="register-password">{t("password")}</InputLabel>
          <OutlinedInput
            id="register-password"
            type={showPassword ? "text" : "password"}
            error={Boolean(formik.touched.password && formik.errors.password)}
            label={t("password")}
            name="password"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.password}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          {Boolean(formik.touched.password && formik.errors.password) && (
            <FormHelperText error>{formik.errors.password}</FormHelperText>
          )}
        </FormControl>
        <FormControl>
          <InputLabel htmlFor="register-confirm-password">
            {t("confirm_password")}
          </InputLabel>
          <OutlinedInput
            id="register-confirm-password"
            type={showPassword ? "text" : "password"}
            error={Boolean(
              formik.touched.passwordConfirmation &&
                formik.errors.passwordConfirmation
            )}
            label={t("confirm_password")}
            name="passwordConfirmation"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.passwordConfirmation}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          {Boolean(
            formik.touched.passwordConfirmation &&
              formik.errors.passwordConfirmation
          ) && (
            <FormHelperText error>
              {formik.errors.passwordConfirmation}
            </FormHelperText>
          )}
        </FormControl>

        {formik.errors.submit && (
          <Box sx={{ mt: 1 }}>
            <FormHelperText error>{formik.errors.submit}</FormHelperText>
          </Box>
        )}
        <Typography color="textSecondary" variant="body2">
          {t("by_clicking_register")}
          {t("terms_and_conditions")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
          }}
        >
          <ReCAPTCHA ref={recaptcha} sitekey={process.env.REACT_APP_SITE_KEY} />
        </Box>
        <Button
          type="submit"
          color="primary"
          size="large"
          variant="contained"
          disabled={formik.isSubmitting}
          sx={{ mt: 5 }}
        >
          {t("CREATE_AN_ACCOUNT")}
        </Button>
      </Stack>
      {/* <Stack spacing={2} sx={{ mt: 10 }} direction="column" alignItems="center">
        <Typography fontSize={14}>{t("social_register_title")}</Typography>
        <Stack spacing={1} direction="row">
          <IconButton size="large">
            <SocialIcon src={googleIconImage} alt="Entrar com Google" />
          </IconButton>
          <IconButton size="large">
            <SocialIcon src={facebookIconImage} alt="Entrar com Facebook" />
          </IconButton>
          <IconButton size="large">
            <SocialIcon src={appleIconImage} alt="Entrar com Apple" />
          </IconButton>
        </Stack>
      </Stack> */}
    </>
  );
};

export default RegisterForm;
